import { ConfirmDialogComponent } from '@/components/confirm-dialog/confirm-dialog.component';
import { FileUploadDialogComponent } from '@/components/file-upload-dialog/file-upload-dialog.component';
import { MaintenanceDialogComponent } from '@/components/maintenance-dialog/maintenance-dialog.component';
import { MaintenanceDoneDialogComponent } from '@/components/maintenance-done-dialog/maintenance-done-dialog.component';
import { PropertyDocViewComponent } from '@/components/property-doc-view/property-doc-view.component';
import { ObjectUpdatedType } from '@/enums/ObjectUpdatedType.enum';
import { MatHelper } from '@/helpers/mat-helper.helper';
import { TrackingHelper } from '@/helpers/tracking.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { AppConfigService } from '@/services/app-config.service';
import { FileUploadType } from '@/services/file-upload.service';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  DocumentCategory,
  PropertyMaintenance,
  PropertyMaintenanceDone,
  RealProperty,
  TrackingService
} from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { PropertyDocViewComponent as PropertyDocViewComponent_1 } from '../../../components/property-doc-view/property-doc-view.component';
import { DetailRowDirective } from '../../../directives/tables/table-detail-row/detail-row.directive';
import { DecimalFormatPipe } from '../../../extensions/DecimalPipe';
import { DateFormatPipe } from '../../../extensions/datePipe';

import { NgFor, NgIf } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-tracking-maintenance',
  templateUrl: './tracking-maintenance.component.html',
  styleUrls: ['./tracking-maintenance.component.scss'],
  animations: MatHelper.getTableAnimations(),
  standalone: true,
  imports: [
    NgIf,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSlideToggleModule,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatMenuModule,
    NgFor,
    DetailRowDirective,
    PropertyDocViewComponent_1,
    DateFormatPipe,
    DecimalFormatPipe
  ]
})
export class TrackingMaintenanceComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() prop: RealProperty;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(PropertyDocViewComponent) docComponent: PropertyDocViewComponent;

  dataSource: MatTableDataSource<PropertyMaintenance>;
  displayFullColumns = false;
  tableClass = 'light';
  lightColumns = ['summary', 'frequency', 'toDoDate', 'actions'];
  fullColumns = ['summary', 'frequency', 'lastDoneDate', 'toDoDate', 'type', 'description', 'actions'];
  displayedColumns = [];
  rawData: PropertyMaintenance[];

  expandedElement: PropertyMaintenance | null;

  category = DocumentCategory.MAINTENANCE;
  canUpdateProperty = false;

  sub = new Subscription();
  updateSub = new Subscription();
  deleteSub = new Subscription();
  deleteDoneSub = new Subscription();

  constructor(
    private trackingService: TrackingService,
    private trackingHelper: TrackingHelper,
    private dialog: MatDialog,
    private alertService: AlertService,
    private appConfigService: AppConfigService,
    private userHelper: UserHelper,
    private changeDetectorRefs: ChangeDetectorRef
  ) {
    this.displayedColumns = this.lightColumns;
  }

  ngOnInit() {
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
    this.loadData();
    this.updateSub = this.appConfigService.objectUpdated.subscribe((type) => {
      if (type === ObjectUpdatedType.DOC) {
        this.refreshDocList();
      } else if (type === ObjectUpdatedType.MAINTENANCE) {
        this.loadData();
        this.refreshDocList();
      }
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.updateSub.unsubscribe();
    this.deleteSub.unsubscribe();
    this.deleteDoneSub.unsubscribe();
  }

  loadData() {
    let filter: string;
    if (this.dataSource && this.dataSource.filter) {
      filter = this.dataSource.filter;
    }
    this.sub = this.trackingService.getRealPropertyMaintenanceList(this.prop.uid).subscribe((data) => {
      this.rawData = data;
      const displayedData = [];
      data.forEach((i) => {
        const row = {
          maintenance: i,
          summary: i.summary,
          lastDoneDate: i.lastDoneDate,
          toDoDate: i.toDoDate,
          description: i.description,
          type: this.trackingHelper.getMaintenanceTypeName(i.type),
          frequency: 'Tous les ' + this.trackingHelper.getMaintenanceFrequencyName(i.frequency),
          isTodo: this.trackingHelper.isMaintenanceToDo(i),
          hasDone: i.dones && i.dones.length > 0
        };
        displayedData.push(row);
      });
      this.dataSource = new MatTableDataSource(displayedData);
      // *ngIf break the sort : must be set in background
      setTimeout(() => (this.dataSource.sort = this.sort));
      if (filter) {
        this.applyFilter(filter);
      }
    });
  }

  ngAfterViewInit() {}

  toggleDisplayColumns() {
    if (this.displayFullColumns) {
      this.displayFullColumns = false;
      this.displayedColumns = this.lightColumns;
      this.tableClass = 'light';
    } else {
      this.displayFullColumns = true;
      this.displayedColumns = this.fullColumns;
      this.tableClass = 'full';
    }
    this.changeDetectorRefs.detectChanges();
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  add() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(MaintenanceDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  edit(maintenanceToEdit: PropertyMaintenance) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(MaintenanceDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop, maintenance: maintenanceToEdit, edition: true }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  confirmDelete(maintenance: PropertyMaintenance) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: "Confirmez-vous la suppression de l'entretien ?"
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete(maintenance);
        }
      });
    }
  }

  doDelete(maintenance: PropertyMaintenance) {
    this.deleteSub = this.trackingService
      .deleteRealPropertyMaintenance(this.prop.uid, maintenance.uid)
      .subscribe((result) => {
        this.alertService.success('Suppression effectuée avec succès');
        this.loadData();
      });
  }

  addDone(maintenance: PropertyMaintenance) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(MaintenanceDoneDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop, maintenance }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  confirmDeleteDone(maintenance: PropertyMaintenance, maintenanceDone: PropertyMaintenanceDone) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: "Confirmez-vous la suppression de l'entretien effectué ?"
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteMaintenanceDone(maintenance, maintenanceDone);
        }
      });
    }
  }

  doDeleteMaintenanceDone(maintenance: PropertyMaintenance, maintenanceDone: PropertyMaintenanceDone) {
    this.deleteDoneSub = this.trackingService
      .deleteRealPropertyMaintenanceDone(this.prop.uid, maintenance.uid, maintenanceDone.uid)
      .subscribe((result) => {
        this.alertService.success('Suppression effectuée avec succès');
        this.loadData();
      });
  }

  uploadDocMaintenance() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(FileUploadDialogComponent, {
        // width: '20rem'
        data: {
          uploadType: FileUploadType.DOC,
          props: [this.prop],
          defaultCategory: DocumentCategory.MAINTENANCE
        }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.refreshDocList();
        }
      });
    }
  }

  refreshDocList() {
    if (this.docComponent) {
      this.docComponent.loadDocList();
    }
  }
}
